import { ComponentType, useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconCheckDefault, IconFileVisualizationOutline } from '@iwoca/icons';
import { Button, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import { TFundingRequirementStatus } from './RequirementItem/RequirementItem';
import { ApplicantCallStep } from '../ApplicantCallStep/ApplicantCallStep';
import { BankDetailsStep } from '../BankDetailsStep/BankDetailsStep';
import { CommissionConsentStep } from '../CommissionConsentStep/CommissionConsentStep';
import { CreditAgreementStep } from '../CreditAgreementStep/CreditAgreementStep';
import { DebitCardStep } from '../DebitCardStep/DebitCardStep';
import { IdCheckStep } from '../IdCheckStep/IdCheckStep';
import { InnerLayout } from '../InnerLayout/InnerLayout';
import { PersonalGuaranteeStep } from '../PersonalGuaranteeStep/PersonalGuaranteeStep';
import { RefinancingSideLetterStep } from '../RefinancingSideLetterStep/RefinancingSideLetterStep';
import { ShareholdersStep } from '../ShareholdersStep/ShareholdersStep';
import { UploadBankEvidenceStep } from '../UploadBankEvidenceStep/UploadBankEvidenceStep';
import {
    useGetFundingRequirements,
    useGetLoanDocumentRequirements
} from '../useGetFundingRequirements/useGetFundingRequirements';
import { useRequirementsProgress } from '../useRequirementsProgress';

// Order matches the old funding-requirements page's PRIORITY_ORDER
// (src/myAccount/funding/fundingSteps/orderFundingRequirements.ts): ID-check
// comes right after the bank steps, ahead of signing/guarantee/commission.
const REQUIREMENT_STEPS: {
    types: string[];
    Component: ComponentType<{ status: TFundingRequirementStatus }>;
}[] = [
    { types: ['BANK_DETAILS'], Component: BankDetailsStep },
    { types: ['UPLOAD_BANK_EVIDENCE'], Component: UploadBankEvidenceStep },
    // The three id-check types collapse into one row; several can be present at once.
    {
        types: [
            'IDVERSE_VERIFICATION',
            'ONFIDO_VERIFICATION',
            'ONFIDO_VIDEO_VERIFICATION'
        ],
        Component: IdCheckStep
    },
    { types: ['MAJOR_SHAREHOLDER_CHECK'], Component: ShareholdersStep },
    { types: ['DEBIT_CARD'], Component: DebitCardStep },
    { types: ['CREDIT_AGREEMENT'], Component: CreditAgreementStep },
    { types: ['PERSONAL_GUARANTEE'], Component: PersonalGuaranteeStep },
    { types: ['COMMISSION_CONSENT'], Component: CommissionConsentStep },
    {
        types: ['REFINANCING_SIDE_LETTER'],
        Component: RefinancingSideLetterStep
    },
    { types: ['APPLICANT_CALL'], Component: ApplicantCallStep }
];

// Lower number = less complete; an aggregated row shows the least-complete status
// so it isn't marked done while a sibling check is still outstanding.
const STATUS_PRIORITY: Record<TFundingRequirementStatus, number> = {
    REQUIRED: 0,
    ERROR: 1,
    PENDING: 2,
    OPTIONAL: 3,
    COMPLETED: 4
};

export const RequirementsList = () => {
    const { fundingRequirements } = useGetFundingRequirements();
    const { loanDocumentRequirements } = useGetLoanDocumentRequirements();

    const allRequirements = [
        ...fundingRequirements,
        ...loanDocumentRequirements
    ];

    const hasErrors = allRequirements.some((req) => req.status === 'ERROR');

    const allCompleted = allRequirements.every(
        (req) => req.status === 'COMPLETED'
    );
    const underReview =
        !allCompleted &&
        allRequirements.every(
            (req) => req.status === 'COMPLETED' || req.status === 'PENDING'
        );

    const history = useHistory();
    const { search } = useLocation();
    const progress = useRequirementsProgress();

    useEffect(() => {
        tracking.ampli.fundingRequirementsViewed({ progress, version: '2.0' });
        (window as any).FS?.event?.('New Funding Requirements Page Visited');
    }, []);

    return (
        <InnerLayout
            title={
                <FormattedMessage
                    id="_.funding_requirements.list.title"
                    defaultMessage="Complete your final checks"
                />
            }
            subheading={
                <FormattedMessage
                    id="_.funding_requirements.list.subheading"
                    defaultMessage="Before we can send funds to your account, we need you to complete a few final steps."
                />
            }
        >
            {allCompleted && (
                <div className="bg-success-light mt-xl flex gap-m rounded-xs border border-success-mid bg-success-lt p-s text-primary-10">
                    <IconCheckDefault className="shrink-0 text-l" />
                    <Text className="text-s font-bold">
                        <FormattedMessage
                            id="_.funding_requirements.list.all_complete_banner"
                            defaultMessage="You’ve completed all the required steps. If you’re happy that the information below is correct, click ‘Confirm and continue’."
                        />
                    </Text>
                </div>
            )}

            {underReview && (
                <div className="mt-xl flex items-start gap-m rounded-xs border border-secondary-80 bg-secondary-95 p-s text-s">
                    <IconFileVisualizationOutline className="mt-xs shrink-0 text-xl" />
                    <div>
                        <Text className="font-bold">
                            <FormattedMessage
                                id="_.funding_requirements.list.under_review_title"
                                defaultMessage="We’re reviewing your supplied information and will get back to you as soon as possible."
                            />
                        </Text>
                        <Text className="mt-m">
                            <FormattedMessage
                                id="_.funding_requirements.list.under_review_contact"
                                defaultMessage="If you have any questions about your application, don’t hesitate to call us on {phoneLink}."
                                values={{
                                    phoneLink: (
                                        <a
                                            href="tel:+442037780105"
                                            className="text-primary-40"
                                        >
                                            020 3778 0105
                                        </a>
                                    )
                                }}
                            />
                        </Text>
                    </div>
                </div>
            )}
            <div className="mt-2xl border-t border-secondary-90">
                {REQUIREMENT_STEPS.map(({ types, Component }) => {
                    const matches = allRequirements.filter((req) =>
                        types.includes(req.type)
                    );
                    const key = types[0];

                    // Applicant call surfaces even with errors: any error on the
                    // application means the customer should be prompted to call.
                    if (types.includes('APPLICANT_CALL')) {
                        if (hasErrors) {
                            return <Component key={key} status="REQUIRED" />;
                        }

                        return matches.length > 0 ? (
                            <Component key={key} status={matches[0]!.status} />
                        ) : null;
                    }

                    if (matches.length === 0) return null;

                    // Aggregate to the least-complete status across matched types
                    // (id-check collapses its three types into one row).
                    const status = matches.reduce(
                        (least, req) =>
                            STATUS_PRIORITY[req.status] < STATUS_PRIORITY[least]
                                ? req.status
                                : least,
                        matches[0]!.status
                    );

                    if (status === 'ERROR') return null;

                    return <Component key={key} status={status} />;
                })}
            </div>
            {(allCompleted || underReview) && (
                <Button
                    variant="primary-rounded"
                    className="mt-2xl"
                    disabled={underReview}
                    onClick={() => {
                        tracking.ampli.fundingRequirementsConfirmClicked({
                            progress,
                            version: '2.0'
                        });
                        history.push(
                            maintainStateKey({
                                pathname: '/confirm-funding/',
                                search
                            })
                        );
                    }}
                >
                    <FormattedMessage
                        id="_.funding_requirements.list.confirm_button"
                        defaultMessage="Confirm and continue"
                    />
                </Button>
            )}
        </InnerLayout>
    );
};
